import React from 'react';
import { Modal, Box, Typography, Button } from '@weave-mui/material';
import { AddM, FileDocumentM } from '@weave-mui/icons-weave';
import { Code as CodeIcon } from '@mui/icons-material';

const OPTION_UPLOAD = 'upload';
const OPTION_CREATE_TEST = 'createTest';
const OPTION_CREATE_FROM_GIT = 'createFromGit';

/**
 * AddTestCaseModal - Modal for choosing how to add test cases (Upload CSV, Create manually, or Create from Git).
 * Clicking an option card immediately triggers the corresponding action.
 *
 * @param {Object} props - Component props
 * @param {boolean} [props.open=true] - Whether the modal is open
 * @param {Function} props.onClose - Called when the modal is closed (e.g. Cancel)
 * @param {Function} props.onUpload - Called when user chooses to upload CSV test cases
 * @param {Function} props.onCreateTest - Called when user chooses to create a test case manually
 * @param {Function} props.onCreateFromGit - Called when user chooses to create test cases from Git
 */
const AddTestCaseModal = ({
  open = true,
  onClose,
  onUpload,
  onCreateTest,
  onCreateFromGit,
}) => {
  const options = [
    {
      id: OPTION_UPLOAD,
      title: 'Upload',
      description: 'Upload a CSV file to create multiple tests',
      icon: AddM,
      onClick: onUpload,
    },
    {
      id: OPTION_CREATE_TEST,
      title: 'Create a test',
      description: 'Create a test case information manually',
      icon: FileDocumentM,
      onClick: onCreateTest,
    },
    {
      id: OPTION_CREATE_FROM_GIT,
      title: 'Create from Git',
      description: 'Import test cases by providing Git repository details',
      icon: CodeIcon,
      onClick: onCreateFromGit,
    },
  ];

  return (
    <Modal
      open={open}
      onClose={onClose}
      maxWidth="md"
      fullWidth
      header="Add test case"
      actions={
        <Box sx={{ display: 'flex', justifyContent: 'flex-end' }}>
          <Button variant="outlined" onClick={onClose}>
            Cancel
          </Button>
        </Box>
      }
      sx={{
        '& .MuiModalWeave-content': {
          padding: '16px 24px !important',
        },
        '& .MuiModalWeave-child': {
          padding: '0px !important',
        },
        '& .MuiModalWeave-box': {
          width: '720px !important',
          maxWidth: '90vw',
        },
        '& .MuiModalWeave-actions': {
          padding: '0px 32px 16px 32px',
        },
        '& .MuiModalWeave-child.MuiBox-root': {
          padding: '16px 32px',
        },
      }}
    >
      <Box
        sx={{
          display: 'flex',
          flexDirection: 'row',
          gap: 2,
          flexWrap: 'wrap',
        }}
      >
        {options.map((opt) => {
          const Icon = opt.icon;
          const handleClick = () => opt.onClick?.();
          return (
            <Box
              key={opt.id}
              onClick={handleClick}
              role="button"
              tabIndex={0}
              onKeyDown={(e) => {
                if (e.key === 'Enter' || e.key === ' ') {
                  e.preventDefault();
                  handleClick();
                }
              }}
              sx={{
                flex: '1 1 140px',
                minWidth: 140,
                border: '1px solid',
                borderColor: 'grey.300',
                borderRadius: 1,
                backgroundColor: 'background.paper',
                p: 2,
                display: 'flex',
                flexDirection: 'column',
                alignItems: 'center',
                justifyContent: 'center',
                cursor: 'pointer',
                transition: 'border-color 0.2s, background-color 0.2s',
                '&:hover': {
                  borderColor: 'primary.main',
                  backgroundColor: 'action.hover',
                },
              }}
            >
              <Box
                sx={{
                  width: 48,
                  height: 48,
                  borderRadius: '50%',
                  bgcolor: 'action.selected',
                  display: 'flex',
                  alignItems: 'center',
                  justifyContent: 'center',
                  mb: 1,
                  '& svg': { fontSize: 28 },
                  color: 'text.primary',
                }}
              >
                <Icon />
              </Box>
              <Typography
                variant="subtitle1"
                fontWeight={600}
                color="text.primary"
              >
                {opt.title}
              </Typography>
              <Typography
                variant="body2"
                color="text.secondary"
                sx={{ textAlign: 'center', mt: 0.5 }}
              >
                {opt.description}
              </Typography>
            </Box>
          );
        })}
      </Box>
    </Modal>
  );
};

export default AddTestCaseModal;
